import React from 'react';

import {isXAxisColumnNameNotNumeric} from '../step-config-settings/StepConfigDisableFieldFunctions';
import {
  FIELD_ORDER_UI_SCHEMA_KEY,
  SECTION_NAME_UI_SCHEMA_KEY,
  SHOW_DIVIDER_LINE_KEY,
  ShapeUISchemaV2,
  CustomComponentEditorProps,
  StepConfigEditorOverride,
} from '../StepConfigEditorTypes';
import CollapsibleColumnSectionEditor from './CollapsibleColumnSectionEditor';

/** @package */
export const BOX_PLOT_X_AXIS_SECTION_UI_SCHEMA: ShapeUISchemaV2 = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'xAxisColumnName',
    'xSortDirection',
    'xAxisMin',
    'xAxisMax',
    'xAxisLabel',
    'pointLabelColumnName',
  ],
  [SHOW_DIVIDER_LINE_KEY]: false,
  [SECTION_NAME_UI_SCHEMA_KEY]: 'X-axis',
  xAxisColumnName: {
    displayName: 'Column',
    configShapePath: ['chartConfig', 'parameters', 'xAxisColumnName'],
  },
  xAxisLabel: {
    displayName: 'Custom axis label',
    configShapePath: ['chartConfig', 'parameters', 'xAxisLabel'],
  },
  xSortDirection: {
    displayName: 'Sort',
    configShapePath: ['chartConfig', 'parameters', 'sortDirection'],
    fieldSettings: {
      displayNames: {
        ASCENDING: 'Ascending',
        DESCENDING: 'Descending',
      },
    },
  },
  xAxisMin: {
    displayName: 'Range min',
    configShapePath: ['chartConfig', 'parameters', 'xAxisMin'],
    disableFn: isXAxisColumnNameNotNumeric,
  },
  xAxisMax: {
    displayName: 'Range max',
    configShapePath: ['chartConfig', 'parameters', 'xAxisMax'],
    disableFn: isXAxisColumnNameNotNumeric,
  },
  pointLabelColumnName: {
    displayName: 'Label points by',
    configShapePath: ['chartConfig', 'parameters', 'pointLabelColumnName'],
    infoText: 'Data from this column will be shown when hovering over a point in the chart.',
    fieldSettings: {
      noColumnSelectedText: 'None',
      allowSelectNull: true,
    },
  },
};

const BOX_PLOT_Y_AXIS_SECTION_UI_SCHEMA: ShapeUISchemaV2 = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: ['yAxisColumnName', 'yAxisMin', 'yAxisMax', 'invertYAxis', 'yAxisLabel'],
  [SHOW_DIVIDER_LINE_KEY]: false,
  [SECTION_NAME_UI_SCHEMA_KEY]: 'Y-axis',
  yAxisColumnName: {
    displayName: 'Column',
    configShapePath: ['chartConfig', 'parameters', 'yAxisColumnName'],
  },
  yAxisLabel: {
    displayName: 'Custom axis label',
    configShapePath: ['chartConfig', 'parameters', 'yAxisLabel'],
  },
  yAxisMin: {
    displayName: 'Range min',
    configShapePath: ['chartConfig', 'parameters', 'yAxisMin'],
  },
  yAxisMax: {
    displayName: 'Range max',
    configShapePath: ['chartConfig', 'parameters', 'yAxisMax'],
  },
  invertYAxis: {
    configShapePath: ['chartConfig', 'parameters', 'invertYAxis'],
    editorOverride: StepConfigEditorOverride.CHECKBOX,
    fieldSettings: {
      checkboxLabel: 'Invert Y-axis',
    },
  },
};

export function BoxPlotXAxisSectionEditor(
  customComponentEditorProps: CustomComponentEditorProps
): JSX.Element {
  return (
    <CollapsibleColumnSectionEditor
      uiSchema={BOX_PLOT_X_AXIS_SECTION_UI_SCHEMA}
      rangeMinSystemName="xAxisMin"
      rangeMaxSystemName="xAxisMax"
      {...customComponentEditorProps}
    />
  );
}

export function BoxPlotYAxisSectionEditor(
  customComponentEditorProps: CustomComponentEditorProps
): JSX.Element {
  return (
    <CollapsibleColumnSectionEditor
      uiSchema={BOX_PLOT_Y_AXIS_SECTION_UI_SCHEMA}
      rangeMinSystemName="yAxisMin"
      rangeMaxSystemName="yAxisMax"
      {...customComponentEditorProps}
    />
  );
}
